import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Select from "react-select";
import { C } from "../reporting/reportingStyles";
import { Badge } from "../reporting/ReportingShared";
import { errorToast, successToast } from "../../shared/Toast";
import { getProductListing } from "../../utils/apiCalls";
import { createCampaign, updateCampaign, estimateCampaignReach, sendTestCampaign } from "../../utils/pushCampaignApi";
import { AUDIENCE_TYPES, DEEP_LINK_SCREENS, LIMITS } from "../../constants/pushCampaigns";

/**
 * Compose / edit a marketing push campaign.
 *
 * One modal for new and existing drafts. The form is plain state (no formik), like every
 * other form in this panel. Three things it does that the API also enforces, so the admin
 * hears about them before pressing Send: the Arabic both-or-neither rule, the image
 * size/type, and the weekly cap (from `limits`). Reach is estimated live as the audience
 * changes so "0 devices" is visible before a send, not after.
 */

const inputStyle = {
    width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, color: C.text, background: C.white, boxSizing: "border-box",
};
const labelStyle = { fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6, display: "flex", justifyContent: "space-between" };
const hintStyle = { fontSize: 11, color: C.textLight, marginTop: 5, lineHeight: 1.4 };
const pill = (active, disabled) => ({
    padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
    border: `1px solid ${active ? C.coral : C.border}`,
    background: active ? C.coralLight : C.white,
    color: active ? C.coralDark : C.textMed,
});
const btnPrimary = {
    padding: "10px 20px", borderRadius: 10, fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer",
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, color: C.white, boxShadow: "0 4px 12px rgba(0,150,137,0.25)",
};
const btnSecondary = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer",
    background: C.white, color: C.textMed, border: `1px solid ${C.border}`,
};

const Counter = ({ value, max, advised }) => {
    const n = (value || "").length;
    const tone = n > max ? C.coral : n > advised ? C.orange : C.textLight;
    return <span style={{ fontSize: 11, color: tone, fontWeight: n > advised ? 700 : 400 }}>{n} / {max}</span>;
};

/** How the notification looks on a phone — title, body, optional image. */
const PhonePreview = ({ title, body, imageUrl, dir }) => (
    <div style={{ background: "rgba(120,120,128,0.16)", borderRadius: 16, padding: "10px 12px", display: "flex", gap: 10, alignItems: "flex-start", maxWidth: 380 }} dir={dir}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, flexShrink: 0 }} />
        <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {title || <em style={{ color: C.textLight }}>Title</em>}
                </div>
                <div style={{ fontSize: 10, color: C.textLight, whiteSpace: "nowrap" }}>now</div>
            </div>
            <div style={{ fontSize: 12, color: C.textMed, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {body || <em style={{ color: C.textLight }}>Body text</em>}
            </div>
            {imageUrl ? <img src={imageUrl} alt="" style={{ marginTop: 8, width: "100%", maxHeight: 120, objectFit: "cover", borderRadius: 8 }} /> : null}
        </div>
    </div>
);

const emptyForm = {
    name: "",
    titleEn: "",
    bodyEn: "",
    withArabic: false,
    titleAr: "",
    bodyAr: "",
    appId: "",
    audienceType: "all",
    days: 30,
    lang: "",
    platform: "",
    screen: "home",
    productId: "",
    sendMode: "draft",
    scheduledAt: "",
    removeImage: false,
};

const fromCampaign = (c) => ({
    ...emptyForm,
    name: c.name || "",
    titleEn: c.titleEn || "",
    bodyEn: c.bodyEn || "",
    withArabic: Boolean(c.titleAr || c.bodyAr),
    titleAr: c.titleAr || "",
    bodyAr: c.bodyAr || "",
    appId: c.appId ? String(c.appId) : "",
    audienceType: c.audience?.type || "all",
    days: c.audience?.days || 30,
    lang: c.audience?.lang || "",
    platform: c.audience?.platform || "",
    screen: c.deepLink?.screen || "home",
    productId: c.deepLink?.productId ? String(c.deepLink.productId) : "",
    sendMode: c.status === "scheduled" ? "schedule" : "draft",
    scheduledAt: c.scheduledAt ? toLocalInput(c.scheduledAt) : "",
});

/** ISO → value for <input type="datetime-local"> in the browser's zone. */
function toLocalInput(iso) {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {number|string} props.merchantId
 * @param {Array<{id,reference,name}>} props.apps   linked apps (from getCampaignApps)
 * @param {object|null} props.limits                { weeklyLimit, usedThisWeek, remaining, nextAllowedAt }
 * @param {object|null} props.campaign              existing draft/scheduled campaign, or null for new
 * @param {(saved: object) => void} props.onSaved
 */
const CampaignComposerModal = ({ isOpen, onClose, merchantId, apps = [], limits, campaign, onSaved }) => {
    const [form, setForm] = useState(emptyForm);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [products, setProducts] = useState([]);
    const [reach, setReach] = useState(null);
    const [reachLoading, setReachLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [confirmSend, setConfirmSend] = useState(false);
    const [testPhone, setTestPhone] = useState("");
    const [testing, setTesting] = useState(false);
    const [capBlocked, setCapBlocked] = useState(null);
    const [errors, setErrors] = useState({});
    const objUrl = useRef(null);

    const isEdit = Boolean(campaign?.id);
    const set = (key, v) => { setForm((f) => ({ ...f, [key]: v })); setErrors((e) => ({ ...e, [key]: undefined })); setCapBlocked(null); };

    useEffect(() => {
        if (!isOpen) return;
        setForm(campaign ? fromCampaign(campaign) : emptyForm);
        setImageFile(null);
        setImagePreview(campaign?.imageUrl || "");
        setErrors({});
        setCapBlocked(null);
        setConfirmSend(false);
        setTestPhone("");
    }, [isOpen, campaign]);

    useEffect(() => () => { if (objUrl.current) URL.revokeObjectURL(objUrl.current); }, []);

    // Product picker options — loaded once per merchant when the modal opens.
    useEffect(() => {
        if (!isOpen || !merchantId || merchantId === "all") return;
        (async () => {
            const p = await getProductListing(merchantId, {}, 1, 1000);
            const list = Array.isArray(p?.products) ? p.products : Array.isArray(p) ? p : [];
            setProducts(list.map((x) => ({ value: String(x.id), label: x.title || x.name || `#${x.id}` })));
        })();
    }, [isOpen, merchantId]);

    const audience = useMemo(() => ({
        type: form.audienceType,
        ...(form.audienceType === "ordered_within" || form.audienceType === "not_ordered_within" ? { days: Number(form.days) || 30 } : {}),
        ...(form.lang ? { lang: form.lang } : {}),
        ...(form.platform ? { platform: form.platform } : {}),
    }), [form.audienceType, form.days, form.lang, form.platform]);

    // Live reach estimate, debounced.
    useEffect(() => {
        if (!isOpen || !merchantId || merchantId === "all") return undefined;
        let cancelled = false;
        setReachLoading(true);
        const t = setTimeout(async () => {
            const r = await estimateCampaignReach({ merchantId, appId: form.appId || null, audience });
            if (cancelled) return;
            setReach(r);
            setReachLoading(false);
        }, 500);
        return () => { cancelled = true; clearTimeout(t); };
    }, [isOpen, merchantId, form.appId, audience]);

    const handleImage = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!["image/jpeg", "image/png"].includes(file.type)) { errorToast("Image must be a JPEG or PNG"); return; }
        if (file.size > LIMITS.imageBytes) { errorToast("Image must be 1 MB or smaller"); return; }
        if (objUrl.current) URL.revokeObjectURL(objUrl.current);
        objUrl.current = URL.createObjectURL(file);
        setImageFile(file);
        setImagePreview(objUrl.current);
        set("removeImage", false);
    };
    const clearImage = () => {
        setImageFile(null);
        setImagePreview("");
        set("removeImage", true);
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Give the campaign a name (only you see it)";
        if (!form.titleEn.trim()) e.titleEn = "Title is required";
        if (!form.bodyEn.trim()) e.bodyEn = "Body is required";
        if (form.titleEn.length > LIMITS.title) e.titleEn = `At most ${LIMITS.title} characters`;
        if (form.bodyEn.length > LIMITS.body) e.bodyEn = `At most ${LIMITS.body} characters`;
        if (form.withArabic) {
            if (!form.titleAr.trim() !== !form.bodyAr.trim()) e.titleAr = "Provide both the Arabic title and body, or turn Arabic off";
        }
        if ((form.audienceType === "ordered_within" || form.audienceType === "not_ordered_within") && !(Number(form.days) >= 1 && Number(form.days) <= 365)) e.days = "Between 1 and 365 days";
        if (form.screen === "product" && !form.productId) e.productId = "Pick the product the notification opens";
        if (form.sendMode === "schedule") {
            if (!form.scheduledAt) e.scheduledAt = "Pick a date and time";
            else if (new Date(form.scheduledAt).getTime() < Date.now() + 60_000) e.scheduledAt = "Must be at least a minute from now";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const payload = (sendModeOverride) => ({
        merchantId,
        appId: form.appId || null,
        name: form.name.trim(),
        titleEn: form.titleEn.trim(),
        bodyEn: form.bodyEn.trim(),
        titleAr: form.withArabic ? form.titleAr.trim() : "",
        bodyAr: form.withArabic ? form.bodyAr.trim() : "",
        audience,
        deepLink: { screen: form.screen, ...(form.screen === "product" ? { productId: Number(form.productId) } : {}) },
        sendMode: sendModeOverride || form.sendMode,
        scheduledAt: (sendModeOverride || form.sendMode) === "schedule" && form.scheduledAt ? new Date(form.scheduledAt).toISOString() : "",
        ...(isEdit && form.removeImage ? { removeImage: true } : {}),
    });

    const persist = async (sendModeOverride) => {
        setSaving(true);
        try {
            const body = payload(sendModeOverride);
            const result = isEdit ? await updateCampaign(campaign.id, body, imageFile) : await createCampaign(body, imageFile);
            if (result?.capBlocked) {
                setCapBlocked({ ...result.capBlocked, message: result.message });
                return null;
            }
            if (result) {
                successToast(body.sendMode === "now" ? "Campaign is being sent" : body.sendMode === "schedule" ? "Campaign scheduled" : "Draft saved");
                onSaved?.(result);
            }
            return result;
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        if (form.sendMode === "now") { setConfirmSend(true); return; }
        const saved = await persist();
        if (saved && !saved.capBlocked) onClose();
    };

    const handleConfirmSend = async () => {
        const saved = await persist("now");
        setConfirmSend(false);
        if (saved && !saved.capBlocked) onClose();
    };

    /** Test send needs a saved row: an unsaved composer is saved as a draft first. */
    const handleTest = async () => {
        if (!validate()) return;
        setTesting(true);
        try {
            let target = campaign;
            if (!isEdit) {
                target = await persist("draft");
                if (!target || target.capBlocked) return;
            } else {
                const saved = await persist(form.sendMode === "now" ? "draft" : undefined);
                if (!saved || saved.capBlocked) return;
                target = saved;
            }
            const r = await sendTestCampaign(target.id, testPhone.trim());
            if (r) successToast(r.message || `Test sent to ${r.sent} device(s)`);
        } finally {
            setTesting(false);
        }
    };

    const wantsSend = form.sendMode === "now" || form.sendMode === "schedule";
    const capReached = Boolean(limits && limits.remaining === 0);
    const reachZero = reach && !reach.error && reach.tokens === 0;
    const primaryLabel = saving ? "Saving…" : form.sendMode === "now" ? "🚀 Send now" : form.sendMode === "schedule" ? "📅 Schedule" : "💾 Save draft";
    const primaryDisabled = saving || (wantsSend && (capReached || reachZero || Boolean(reach?.error)));

    const previewTitle = form.withArabic && form.titleAr ? form.titleAr : form.titleEn;
    const previewBody = form.withArabic && form.bodyAr ? form.bodyAr : form.bodyEn;

    return (
        <Modal isOpen={isOpen} toggle={saving ? undefined : onClose} size="xl">
            <ModalHeader toggle={saving ? undefined : onClose}>{isEdit ? "Edit campaign" : "New push campaign"}</ModalHeader>
            <ModalBody>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 340px", gap: 24, alignItems: "start" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                        {/* Name */}
                        <div>
                            <div style={labelStyle}><span>Campaign name</span><span style={{ fontSize: 11, color: C.textLight }}>internal — customers never see it</span></div>
                            <input style={inputStyle} value={form.name} maxLength={LIMITS.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Weekend espresso offer" />
                            {errors.name && <div style={{ ...hintStyle, color: C.coral }}>{errors.name}</div>}
                        </div>

                        {/* Message */}
                        <div style={{ padding: 14, border: `1px solid ${C.borderLight}`, borderRadius: 12 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Message</div>
                                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.textMed, cursor: "pointer", margin: 0 }}>
                                    <input type="checkbox" checked={form.withArabic} onChange={(e) => set("withArabic", e.target.checked)} /> Add Arabic version
                                </label>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: form.withArabic ? "1fr 1fr" : "1fr", gap: 14 }}>
                                <div>
                                    <div style={labelStyle}><span>Title (English)</span><Counter value={form.titleEn} max={LIMITS.title} advised={LIMITS.titleAdvised} /></div>
                                    <input style={inputStyle} value={form.titleEn} maxLength={LIMITS.title} onChange={(e) => set("titleEn", e.target.value)} placeholder="Free espresso this weekend" />
                                    {errors.titleEn && <div style={{ ...hintStyle, color: C.coral }}>{errors.titleEn}</div>}
                                    <div style={{ ...labelStyle, marginTop: 12 }}><span>Body (English)</span><Counter value={form.bodyEn} max={LIMITS.body} advised={LIMITS.bodyAdvised} /></div>
                                    <textarea style={{ ...inputStyle, minHeight: 84, resize: "vertical" }} value={form.bodyEn} maxLength={LIMITS.body} onChange={(e) => set("bodyEn", e.target.value)} placeholder="Show this notification at the counter and get a free espresso with any pastry." />
                                    {errors.bodyEn && <div style={{ ...hintStyle, color: C.coral }}>{errors.bodyEn}</div>}
                                    <div style={hintStyle}>Lock screens show roughly the first {LIMITS.titleAdvised} characters of the title and {LIMITS.bodyAdvised} of the body.</div>
                                </div>
                                {form.withArabic && (
                                    <div>
                                        <div style={labelStyle}><span>Title (Arabic)</span><Counter value={form.titleAr} max={LIMITS.title} advised={LIMITS.titleAdvised} /></div>
                                        <input style={inputStyle} dir="rtl" value={form.titleAr} maxLength={LIMITS.title} onChange={(e) => set("titleAr", e.target.value)} />
                                        <div style={{ ...labelStyle, marginTop: 12 }}><span>Body (Arabic)</span><Counter value={form.bodyAr} max={LIMITS.body} advised={LIMITS.bodyAdvised} /></div>
                                        <textarea style={{ ...inputStyle, minHeight: 84, resize: "vertical" }} dir="rtl" value={form.bodyAr} maxLength={LIMITS.body} onChange={(e) => set("bodyAr", e.target.value)} />
                                        {errors.titleAr && <div style={{ ...hintStyle, color: C.coral }}>{errors.titleAr}</div>}
                                        <div style={hintStyle}>Sent to customers whose app language is Arabic; everyone else gets the English version.</div>
                                    </div>
                                )}
                            </div>

                            <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Image <Badge color={C.textMed} bg={C.borderLight}>Android only</Badge></div>
                                <label style={{ ...btnSecondary, padding: "7px 14px", fontSize: 12, margin: 0 }}>
                                    {imagePreview ? "Replace…" : "Choose…"}
                                    <input type="file" accept="image/jpeg,image/png" onChange={handleImage} style={{ display: "none" }} />
                                </label>
                                {imagePreview && <button type="button" onClick={clearImage} style={{ ...btnSecondary, padding: "7px 14px", fontSize: 12, color: C.coral }}>Remove</button>}
                                <span style={{ fontSize: 11, color: C.textLight }}>JPEG/PNG, up to 1 MB. iPhones show the text only.</span>
                            </div>
                        </div>

                        {/* Audience */}
                        <div style={{ padding: 14, border: `1px solid ${C.borderLight}`, borderRadius: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>Who receives it</div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                {AUDIENCE_TYPES.map((a) => (
                                    <button key={a.value} type="button" onClick={() => set("audienceType", a.value)} style={pill(form.audienceType === a.value)} title={a.hint}>{a.label}</button>
                                ))}
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginTop: 14 }}>
                                {(form.audienceType === "ordered_within" || form.audienceType === "not_ordered_within") && (
                                    <div>
                                        <div style={labelStyle}><span>Days</span></div>
                                        <input type="number" min={1} max={365} style={inputStyle} value={form.days} onChange={(e) => set("days", e.target.value)} />
                                        {errors.days && <div style={{ ...hintStyle, color: C.coral }}>{errors.days}</div>}
                                    </div>
                                )}
                                <div>
                                    <div style={labelStyle}><span>Language</span></div>
                                    <select style={inputStyle} value={form.lang} onChange={(e) => set("lang", e.target.value)}>
                                        <option value="">Any</option><option value="en">English</option><option value="ar">Arabic</option>
                                    </select>
                                </div>
                                <div>
                                    <div style={labelStyle}><span>Platform</span></div>
                                    <select style={inputStyle} value={form.platform} onChange={(e) => set("platform", e.target.value)}>
                                        <option value="">Any</option><option value="ios">iOS</option><option value="android">Android</option>
                                    </select>
                                </div>
                                <div>
                                    <div style={labelStyle}><span>App</span></div>
                                    <select style={inputStyle} value={form.appId} onChange={(e) => set("appId", e.target.value)}>
                                        <option value="">All linked apps ({apps.length})</option>
                                        {apps.map((a) => <option key={a.id} value={a.id}>{a.name || a.reference}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: 8, background: C.bg, fontSize: 12, color: C.textMed, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                                <span style={{ fontWeight: 700, color: C.text }}>Estimated reach</span>
                                {reachLoading ? <span>calculating…</span>
                                    : reach?.error ? <span style={{ color: C.coral, fontWeight: 600 }}>{reach.error.message}</span>
                                        : reach ? (
                                            <>
                                                <span>≈ <b style={{ color: C.text }}>{reach.users.toLocaleString()}</b> customer{reach.users === 1 ? "" : "s"}</span>
                                                <span><b style={{ color: C.text }}>{reach.tokens.toLocaleString()}</b> device{reach.tokens === 1 ? "" : "s"}</span>
                                                <span style={{ color: C.textLight }} title={reach.byPlatform.unknown ? "Unknown = devices registered before the platform was recorded; they update on the customer's next app launch." : undefined}>
                                                    iOS {reach.byPlatform.ios} · Android {reach.byPlatform.android}{reach.byPlatform.unknown ? ` · unknown ${reach.byPlatform.unknown}` : ""}
                                                </span>
                                                {reach.tokens === 0 && <span style={{ color: C.coral, fontWeight: 600 }}>Nobody would receive this.</span>}
                                                {form.platform && reach.unknownExcluded > 0 && (
                                                    <span style={{ color: C.orange, fontWeight: 600, flexBasis: "100%" }}>
                                                        {reach.unknownExcluded.toLocaleString()} device{reach.unknownExcluded === 1 ? "" : "s"} of unknown platform excluded by the {form.platform === "ios" ? "iOS" : "Android"} filter (registered before the platform was recorded). Set Platform to Any to include {reach.unknownExcluded === 1 ? "it" : "them"}.
                                                    </span>
                                                )}
                                                {reach.linkedUsers > reach.users && (
                                                    <span style={{ color: C.textLight, flexBasis: "100%" }}>
                                                        {(reach.linkedUsers - reach.users).toLocaleString()} more linked customer{reach.linkedUsers - reach.users === 1 ? "" : "s"} match these filters but {reach.linkedUsers - reach.users === 1 ? "has" : "have"} no registered {form.platform ? `${form.platform === "ios" ? "iOS" : "Android"} device` : "device"} in the app, so {reach.linkedUsers - reach.users === 1 ? "it" : "they"} cannot be reached.
                                                    </span>
                                                )}
                                            </>
                                        ) : null}
                            </div>
                        </div>

                        {/* Deep link */}
                        <div style={{ padding: 14, border: `1px solid ${C.borderLight}`, borderRadius: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>When tapped, open</div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                {DEEP_LINK_SCREENS.map((s) => (
                                    <button key={s.value} type="button" onClick={() => set("screen", s.value)} style={pill(form.screen === s.value)}>{s.label}</button>
                                ))}
                            </div>
                            {form.screen === "product" && (
                                <div style={{ marginTop: 12, maxWidth: 420 }}>
                                    <Select
                                        options={products}
                                        value={products.find((p) => p.value === form.productId) || null}
                                        onChange={(opt) => set("productId", opt?.value || "")}
                                        placeholder="Search a product…"
                                        isClearable
                                    />
                                    {errors.productId && <div style={{ ...hintStyle, color: C.coral }}>{errors.productId}</div>}
                                </div>
                            )}
                            <div style={hintStyle}>Customers on an older app version land on their Orders tab regardless of this choice.</div>
                        </div>

                        {/* Delivery */}
                        <div style={{ padding: 14, border: `1px solid ${C.borderLight}`, borderRadius: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>Delivery</div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button type="button" onClick={() => set("sendMode", "draft")} style={pill(form.sendMode === "draft")}>Save as draft</button>
                                <button type="button" onClick={() => set("sendMode", "now")} style={pill(form.sendMode === "now", capReached)} disabled={capReached}>Send now</button>
                                <button type="button" onClick={() => set("sendMode", "schedule")} style={pill(form.sendMode === "schedule", capReached)} disabled={capReached}>Schedule</button>
                            </div>
                            {form.sendMode === "schedule" && (
                                <div style={{ marginTop: 12, maxWidth: 320 }}>
                                    <input type="datetime-local" style={inputStyle} value={form.scheduledAt} onChange={(e) => set("scheduledAt", e.target.value)} />
                                    {errors.scheduledAt && <div style={{ ...hintStyle, color: C.coral }}>{errors.scheduledAt}</div>}
                                    <div style={hintStyle}>In your browser's time zone ({Intl.DateTimeFormat().resolvedOptions().timeZone}). Up to 90 days ahead.</div>
                                </div>
                            )}
                            {limits && (
                                <div style={{ ...hintStyle, marginTop: 10, color: capReached ? C.coral : C.textLight, fontWeight: capReached ? 700 : 400 }}>
                                    Sends this week: {limits.usedThisWeek} of {limits.weeklyLimit}
                                    {capReached && limits.nextAllowedAt ? ` — next slot frees ${new Date(limits.nextAllowedAt).toLocaleString()}. You can still save a draft.` : ""}
                                </div>
                            )}
                            {capBlocked && (
                                <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: C.coralLight, color: C.coral, fontSize: 12, fontWeight: 600 }}>
                                    {capBlocked.message}{capBlocked.nextAllowedAt ? ` Next slot frees ${new Date(capBlocked.nextAllowedAt).toLocaleString()}.` : ""}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Preview + test */}
                    <div style={{ position: "sticky", top: 0, display: "flex", flexDirection: "column", gap: 14 }}>
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Preview</div>
                            <PhonePreview title={form.titleEn} body={form.bodyEn} imageUrl={imagePreview} />
                            {form.withArabic && (form.titleAr || form.bodyAr) && (
                                <div style={{ marginTop: 10 }}>
                                    <PhonePreview title={form.titleAr} body={form.bodyAr} imageUrl={imagePreview} dir="rtl" />
                                </div>
                            )}
                            {!previewTitle && !previewBody ? null : (
                                <div style={hintStyle}>Android shows the image; iPhone shows title and body only.</div>
                            )}
                        </div>
                        <div style={{ padding: 12, border: `1px dashed ${C.border}`, borderRadius: 12 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>Send a test to a phone</div>
                            <input style={inputStyle} value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="Customer phone number (signed in to the app)" />
                            <button type="button" onClick={handleTest} disabled={testing || saving} style={{ ...btnSecondary, marginTop: 8, width: "100%", opacity: testing ? 0.7 : 1 }}>
                                {testing ? "Sending test…" : "📲 Send test"}
                            </button>
                            <div style={hintStyle}>Saves the campaign as a draft first if needed. Goes only to that customer's devices; does not count against the weekly limit.</div>
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                <button type="button" onClick={onClose} disabled={saving} style={btnSecondary}>Cancel</button>
                <button type="button" onClick={handleSubmit} disabled={primaryDisabled} style={{ ...btnPrimary, opacity: primaryDisabled ? 0.55 : 1, cursor: primaryDisabled ? "default" : "pointer" }}>
                    {primaryLabel}
                </button>
            </ModalFooter>

            <Modal isOpen={confirmSend} toggle={() => !saving && setConfirmSend(false)}>
                <ModalHeader toggle={() => !saving && setConfirmSend(false)}>Send this campaign now?</ModalHeader>
                <ModalBody style={{ color: C.textMed, lineHeight: 1.6 }}>
                    <p style={{ margin: 0 }}>
                        “<b style={{ color: C.text }}>{form.titleEn}</b>” goes to approximately{" "}
                        <b style={{ color: C.text }}>{reach?.users?.toLocaleString?.() ?? "…"}</b> customer{reach?.users === 1 ? "" : "s"} on{" "}
                        <b style={{ color: C.text }}>{reach?.tokens?.toLocaleString?.() ?? "…"}</b> device{reach?.tokens === 1 ? "" : "s"}. This cannot be recalled once delivered.
                    </p>
                    {limits && <p style={{ margin: "10px 0 0", fontSize: 12 }}>Uses 1 of {limits.weeklyLimit} sends this week ({limits.usedThisWeek} used so far).</p>}
                </ModalBody>
                <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                    <button type="button" onClick={() => setConfirmSend(false)} disabled={saving} style={btnSecondary}>Back</button>
                    <button type="button" onClick={handleConfirmSend} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>{saving ? "Sending…" : "Yes, send now"}</button>
                </ModalFooter>
            </Modal>
        </Modal>
    );
};

export default CampaignComposerModal;
